// Opponent presence visuals, extracted from GameBoard.jsx: the
// face-down opponent hand fan meshes, the floating player name badges
// (canvas-rendered sprites), and the seat-perspective camera flip that
// orients the table for the local seat.

import { CARD_HEIGHT, CARD_THICKNESS, CARD_WIDTH, createHandBackMesh, createHandFaceMesh } from '../../../utils/game/cardMesh';
import * as THREE from 'three';

// Replay-only enlargement of the far seat's hand fan.
const REPLAY_HAND_SCALE = 1.6;

// ── Opponent hand: 3D face-down cards on the table ──────────────
//
// Lays out N face-down card meshes near the opponent's table edge so
// the local player can see how many cards the opponent is holding
// (and whether any are Sites) without revealing the faces. Called
// whenever `opponentHand` state changes via the `hand:info` sync
// message. The meshes have no physics and no interactivity. They're
// purely a visual indicator, like looking across a table and seeing
// the back of someone's fanned hand.
export function updateOpponentHandMeshes(host) {
  if (!host.scene) return;

  const { opponentHand } = host.state;

  // Remove old meshes.
  for (const mesh of host.opponentHandMeshes) {
    host.scene.scene.remove(mesh);
    mesh.geometry.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m.dispose());
  }
  host.opponentHandMeshes = [];

  const N = opponentHand.length;
  if (N === 0) return;

  // ── Position the fan BEHIND the opponent's table edge ──
  // Table half-depth is ~70 units; placing the hand at ±82 puts it
  // ~12 units past the edge so it's visually "off the board." The
  // same place a real player would hold their cards.
  // In a replay the far hand is fully revealed and is something the
  // viewer is meant to read, not just count. Blow it up so the faces are
  // legible from the spectator camera, and push the fan back by the extra
  // half-height so its near edge stays off the table.
  const scale = host.props.replayDriver ? REPLAY_HAND_SCALE : 1;
  const baseZ = 82 + ((scale - 1) * CARD_HEIGHT) / 2;
  const opponentZ = host.state.isHost ? -baseZ : baseZ;
  const opponentRotation = host.state.isHost ? 0 : Math.PI;

  // Fan geometry. Spread cards across up to ~60 world units, tapering
  // the angle per card as the hand grows so it stays readable.
  const maxSpread = 50 * scale;
  const spacing = Math.min(CARD_WIDTH * scale * 0.65, maxSpread / Math.max(N, 1));
  const totalWidth = spacing * (N - 1);
  const startX = -totalWidth / 2;

  // Subtle arc rotation. Cards at the edges tilt outward slightly.
  const maxTilt = N <= 3 ? 3 : N <= 6 ? 5 : N <= 10 ? 8 : 10; // degrees total arc
  const tiltPerCard = N > 1 ? maxTilt / (N - 1) : 0;

  for (let i = 0; i < N; i++) {
    const card = opponentHand[i];
    const mesh = card.revealed && card.imageUrl
      ? createHandFaceMesh(card)
      : createHandBackMesh(card.isSite, !!card._spellbookBack);

    const x = startX + i * spacing;
    const tiltDeg = N === 1 ? 0 : -maxTilt / 2 + i * tiltPerCard;
    const tiltRad = (tiltDeg * Math.PI) / 180;

    // Lay flat: rotation.x = -π/2 puts +Z (card face) up.
    // rotation.z encodes the fan tilt + player orientation.
    mesh.rotation.x = -Math.PI / 2;
    mesh.rotation.z = opponentRotation + tiltRad;
    mesh.scale.setScalar(scale);

    // Slight vertical stacking so overlapping cards don't z-fight.
    const y = (CARD_THICKNESS * scale) / 2 + 0.1 + i * 0.05;
    mesh.position.set(x, y, opponentZ);

    host.scene.scene.add(mesh);
    host.opponentHandMeshes.push(mesh);
  }
  host.updatePlayerBadges();
}

// ── 3D player nameplates behind each hand ─────────────────────
// Canvas-rendered badge (avatar circle + name) placed in the Three.js
// scene behind the hand cards so they sit in the world, not as HTML.
export function createBadgeSprite(host, name, avatarUrl, borderColor) {
  // Layout: circular avatar on top, pill name label below.
  // Canvas is square-ish to fit both vertically.
  const S = 256; // avatar circle diameter in canvas pixels
  const PAD = 16;
  const PILL_H = 48;
  const W = S + PAD * 2; // 288
  const H = S + PILL_H + PAD * 3; // 336
  const cx = W / 2;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Avatar circle placeholder (dark fill until image loads)
  const avatarY = PAD;
  const avatarR = S / 2;
  ctx.fillStyle = 'rgba(10, 9, 7, 0.85)';
  ctx.beginPath();
  ctx.arc(cx, avatarY + avatarR, avatarR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(cx, avatarY + avatarR, avatarR - 2, 0, Math.PI * 2);
  ctx.stroke();

  // Name pill below the avatar
  const pillY = avatarY + S + PAD;
  const pillW = Math.min(W - PAD * 2, 240);
  const pillX = (W - pillW) / 2;
  const pillR = PILL_H / 2;
  ctx.fillStyle = 'rgba(10, 9, 7, 0.85)';
  ctx.beginPath();
  ctx.roundRect(pillX, pillY, pillW, PILL_H, pillR);
  ctx.fill();
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(pillX + 1, pillY + 1, pillW - 2, PILL_H - 2, pillR - 1);
  ctx.stroke();

  ctx.fillStyle = '#e8d5a0';
  ctx.font = 'bold 32px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name || '', cx, pillY + PILL_H / 2);

  const tex = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  // Keep aspect ratio: W:H = 288:336 ≈ 0.857:1
  const worldH = 12;
  sprite.scale.set(worldH * (W / H), worldH, 1);
  sprite.renderOrder = 998;

  // Load avatar image async and draw into the circle
  if (avatarUrl) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, avatarY + avatarR, avatarR - 4, 0, Math.PI * 2);
      ctx.clip();
      // Cover-fit: draw the largest centered square from the image
      const imgS = Math.min(img.width, img.height);
      const sx = (img.width - imgS) / 2;
      const sy = 0; // top-aligned for portraits
      ctx.drawImage(img, sx, sy, imgS, imgS,
        cx - avatarR + 4, avatarY + 4, S - 8, S - 8);
      ctx.restore();
      tex.needsUpdate = true;
    };
    img.src = avatarUrl;
  }

  return sprite;
}

export function disposeBadge(host, mesh) {
  if (!mesh) return;
  host.scene?.scene.remove(mesh);
  mesh.material.map?.dispose();
  mesh.material.dispose();
}

export function updatePlayerBadges(host) {
  if (!host.scene) return;
  const isHost = host.state.isHost;
  const localZ = isHost ? 88 : -88;
  // The replay fan is enlarged (see updateOpponentHandMeshes), and the badge
  // sprite draws with depthTest off, so it would sit on top of the revealed
  // faces. Push it clear of the fan's far edge.
  const remoteDist = host.props.replayDriver
    ? 82 + (REPLAY_HAND_SCALE * CARD_HEIGHT) / 2 + 2
    : 88;
  const remoteZ = isHost ? -remoteDist : remoteDist;

  // Local player badge
  const localInfo = host.props.arenaPlayerInfo;
  if (localInfo?.name && !host._playerBadgeMesh) {
    host._playerBadgeMesh = host._createBadgeSprite(
      localInfo.name, localInfo.avatarUrl, 'rgba(180,140,60,0.5)',
    );
    host._playerBadgeMesh.position.set(0, 0.5, localZ);
    host.scene.scene.add(host._playerBadgeMesh);
  }
  if (host._playerBadgeMesh) {
    host._playerBadgeMesh.position.z = localZ;
  }

  // Opponent badge
  const oppInfo = host.props.arenaOpponentInfo;
  if (oppInfo?.name && !host._opponentBadgeMesh) {
    host._opponentBadgeMesh = host._createBadgeSprite(
      oppInfo.name, oppInfo.avatarUrl, 'rgba(180,60,60,0.5)',
    );
    host._opponentBadgeMesh.position.set(0, 0.5, remoteZ);
    host.scene.scene.add(host._opponentBadgeMesh);
  }
  if (host._opponentBadgeMesh) {
    host._opponentBadgeMesh.position.z = remoteZ;
  }
}

// Single funnel for every seat-dependent imperative scene write. The
// authoritative seat (isHost) arrives async during the handshake, while
// `this.state.isHost` defaults to `true`. Render-path reads self-heal when
// the value commits, but imperative Three.js writes do not. So the camera,
// 3D name badges, and ownership outlines must all be (re)applied through
// here whenever the seat resolves (and again on resume/reconnect).
// Consolidating them in one place is what keeps a guest's badges from being
// stranded at the host orientation (the "mirrored player blob" bug).
export function applySeatPerspective(host, isHost) {
  // Symmetric: host faces theta 0, guest is rotated 180°. The old code only
  // ever rotated for the guest and never reset for the host, so a reused
  // orbit could desync from the badge/outline placement.
  host.scene?.setOrbitTheta(isHost ? 0 : Math.PI);
  host.updatePlayerBadges();
  host.refreshOwnershipOutlines();
}
